import { useEffect, useState } from 'react'
import { Check, Loader2, Plus, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input, type InputProps } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/stores/auth-store'
import { useSettingsStore } from '@/stores/settings-store'
import type { OrgProfile } from '@/lib/schemas'

/**
 * Company-profile fields stored as `string[]` but edited as one comma-separated
 * text input. The raw text is held separately while the user types and only
 * parsed into the profile at dirty-check / save time — round-tripping every
 * keystroke through the array dropped a trailing comma or space instantly.
 */
type ListField =
  | 'primaryServices'
  | 'industries'
  | 'geographicCoverage'
  | 'differentiators'
  | 'safetyMilestones'
type ListFieldText = Record<ListField, string>

const listToText = (arr: string[] | undefined): string => arr?.join(', ') || ''

const textToList = (text: string): string[] =>
  text
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0)

const listFieldTextFromProfile = (profile: Partial<OrgProfile>): ListFieldText => ({
  primaryServices: listToText(profile.primaryServices),
  industries: listToText(profile.industries),
  geographicCoverage: listToText(profile.geographicCoverage),
  differentiators: listToText(profile.differentiators),
  safetyMilestones: listToText(profile.safetyRecord?.safetyMilestones),
})

const withListFields = (
  profile: Partial<OrgProfile>,
  text: ListFieldText,
): Partial<OrgProfile> => ({
  ...profile,
  primaryServices: textToList(text.primaryServices),
  industries: textToList(text.industries),
  geographicCoverage: textToList(text.geographicCoverage),
  differentiators: textToList(text.differentiators),
  safetyRecord: {
    ...profile.safetyRecord,
    safetyMilestones: textToList(text.safetyMilestones),
  },
})

interface ProfileInputProps extends InputProps {
  isReadOnly: boolean
}

/**
 * Company-profile input. Read-only for non-admins (the server only lets an org
 * admin save the profile): `readOnly`, not `disabled`, so values stay at full
 * contrast and can still be selected and copied. Placeholders are dropped in
 * that state so an empty field isn't mistaken for a saved value.
 */
function ProfileInput({ isReadOnly, placeholder, className, ...props }: ProfileInputProps) {
  return (
    <Input
      {...props}
      readOnly={isReadOnly}
      placeholder={isReadOnly ? undefined : placeholder}
      className={cn(
        isReadOnly &&
          'bg-muted/40 cursor-default focus-visible:ring-0 focus-visible:border-border',
        className,
      )}
    />
  )
}

/**
 * The organisation's company profile form. Moved out of Settings into
 * Knowledge (R4) unchanged: the same fields, the same save, and the same
 * admin-only rule, which the API enforces (`PATCH /settings/profile`
 * requires the admin role).
 */
export function CompanyProfileForm() {
  const { organization, isLoading, isSaving, error, loadSettings, saveProfile, clearError } =
    useSettingsStore()
  // Same rule as `PATCH /settings/profile` (requireRole 'admin'): only an org
  // admin can save. Platform-admin status does not grant it.
  const role = useAuthStore((state) => state.role)
  const isProfileReadOnly = role !== 'admin'

  const [profileForm, setProfileForm] = useState<Partial<OrgProfile>>({})
  const [listFieldText, setListFieldText] = useState<ListFieldText>(() =>
    listFieldTextFromProfile({}),
  )
  // Saved snapshot for dirty detection
  const [savedProfileForm, setSavedProfileForm] = useState<Partial<OrgProfile>>({})
  // Brief "Saved" confirmation flash
  const [profileSaved, setProfileSaved] = useState(false)

  // The profile as it would be saved: form state plus the parsed list-field text
  const profileToSave = withListFields(profileForm, listFieldText)
  const isProfileDirty = JSON.stringify(profileToSave) !== JSON.stringify(savedProfileForm)

  useEffect(() => {
    void loadSettings()
  }, [loadSettings])

  // Sync the form when organisation data loads (initial load or after save)
  useEffect(() => {
    if (organization) {
      const profile = organization.profile || {}
      const profileState = {
        ...profile,
        primaryServices: profile.primaryServices || [],
        industries: profile.industries || [],
        geographicCoverage: profile.geographicCoverage || [],
        differentiators: profile.differentiators || [],
        certifications: profile.certifications || [],
        safetyRecord: profile.safetyRecord || {},
        entityDetails: profile.entityDetails || {},
        insurances: profile.insurances || [],
        referees: profile.referees || [],
      }
      const listText = listFieldTextFromProfile(profileState)
      setProfileForm(profileState)
      setListFieldText(listText)
      // Snapshot in the same shape `profileToSave` takes, so a fresh load isn't dirty
      setSavedProfileForm(withListFields(profileState, listText))
    }
  }, [organization])

  const handleListFieldChange = (field: ListField, value: string) => {
    setListFieldText((prev) => ({ ...prev, [field]: value }))
  }

  const handleSaveProfile = async (e: React.FormEvent) => {
    e.preventDefault()
    if (isProfileReadOnly) return
    const success = await saveProfile(profileToSave)
    if (success) {
      setProfileSaved(true)
      setTimeout(() => setProfileSaved(false), 2000)
    }
  }

  const handleAddCertification = () => {
    const certs = profileForm.certifications || []
    setProfileForm({
      ...profileForm,
      certifications: [...certs, { name: '', expiryDate: undefined }],
    })
  }

  const handleRemoveCertification = (index: number) => {
    const certs = profileForm.certifications || []
    setProfileForm({
      ...profileForm,
      certifications: certs.filter((_, i) => i !== index),
    })
  }

  const handleCertificationChange = (
    index: number,
    field: 'name' | 'expiryDate',
    value: string
  ) => {
    const certs = [...(profileForm.certifications || [])]
    const existingCert = certs[index] || { name: '' }
    // `name` is required — clearing it must yield '' (keeps the input controlled),
    // not undefined.
    certs[index] = { ...existingCert, [field]: field === 'name' ? value : value || undefined }
    setProfileForm({ ...profileForm, certifications: certs })
  }

  type Insurance = NonNullable<OrgProfile['insurances']>[number]
  type Referee = NonNullable<OrgProfile['referees']>[number]

  const handleEntityDetailChange = (
    field: keyof NonNullable<OrgProfile['entityDetails']>,
    value: string
  ) => {
    setProfileForm({
      ...profileForm,
      entityDetails: { ...profileForm.entityDetails, [field]: value },
    })
  }

  const handleAddInsurance = () => {
    setProfileForm({
      ...profileForm,
      insurances: [...(profileForm.insurances || []), { type: '' }],
    })
  }

  const handleRemoveInsurance = (index: number) => {
    setProfileForm({
      ...profileForm,
      insurances: (profileForm.insurances || []).filter((_, i) => i !== index),
    })
  }

  const handleInsuranceChange = (index: number, field: keyof Insurance, value: string) => {
    const insurances = [...(profileForm.insurances || [])]
    const existing = insurances[index] || { type: '' }
    insurances[index] = { ...existing, [field]: field === 'type' ? value : value || undefined }
    setProfileForm({ ...profileForm, insurances })
  }

  const handleAddReferee = () => {
    setProfileForm({
      ...profileForm,
      referees: [...(profileForm.referees || []), { name: '' }],
    })
  }

  const handleRemoveReferee = (index: number) => {
    setProfileForm({
      ...profileForm,
      referees: (profileForm.referees || []).filter((_, i) => i !== index),
    })
  }

  const handleRefereeChange = (index: number, field: keyof Referee, value: string) => {
    const referees = [...(profileForm.referees || [])]
    const existing = referees[index] || { name: '' }
    referees[index] = { ...existing, [field]: field === 'name' ? value : value || undefined }
    setProfileForm({ ...profileForm, referees })
  }

  if (isLoading && !organization) {
    return (
      <div className="flex flex-col h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground mt-2">Loading company profile...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="rounded-lg px-4 py-2 bg-destructive/10 text-destructive text-sm flex items-center justify-between">
          <span>{error}</span>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            aria-label="Dismiss error"
            onClick={clearError}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}

      <form onSubmit={(e) => void handleSaveProfile(e)} className="space-y-6">
        {isProfileReadOnly && (
          <p className="text-meta">Only an organisation admin can change the company profile.</p>
        )}

        {/* Basic Information */}
        <section className="space-y-4">
          <h2 className="text-sm font-medium">Basic Information</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="companyName" className="text-meta">
                Company Name
              </Label>
              <ProfileInput
                isReadOnly={isProfileReadOnly}
                id="companyName"
                value={profileForm.companyName || ''}
                onChange={(e) =>
                  setProfileForm({ ...profileForm, companyName: e.target.value })
                }
                placeholder="Your Company Pty Ltd"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tradingAs" className="text-meta">
                Trading As
              </Label>
              <ProfileInput
                isReadOnly={isProfileReadOnly}
                id="tradingAs"
                value={profileForm.tradingAs || ''}
                onChange={(e) =>
                  setProfileForm({ ...profileForm, tradingAs: e.target.value })
                }
                placeholder="Company Brand Name"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="abn" className="text-meta">
              ABN
            </Label>
            <ProfileInput
              isReadOnly={isProfileReadOnly}
              id="abn"
              value={profileForm.abn || ''}
              onChange={(e) =>
                setProfileForm({ ...profileForm, abn: e.target.value })
              }
              placeholder="12 345 678 901"
            />
          </div>
        </section>

        {/* Business Details */}
        <section className="space-y-4">
          <h2 className="text-sm font-medium">Business Details</h2>
          <div className="space-y-2">
            <Label htmlFor="primaryServices" className="text-meta">
              Primary Services
            </Label>
            <ProfileInput
              isReadOnly={isProfileReadOnly}
              id="primaryServices"
              value={listFieldText.primaryServices}
              onChange={(e) => handleListFieldChange('primaryServices', e.target.value)}
              placeholder="Engineering, Construction, Project Management (comma-separated)"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="industries" className="text-meta">
              Industries
            </Label>
            <ProfileInput
              isReadOnly={isProfileReadOnly}
              id="industries"
              value={listFieldText.industries}
              onChange={(e) => handleListFieldChange('industries', e.target.value)}
              placeholder="Infrastructure, Mining, Energy (comma-separated)"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="geographicCoverage" className="text-meta">
              Geographic Coverage
            </Label>
            <ProfileInput
              isReadOnly={isProfileReadOnly}
              id="geographicCoverage"
              value={listFieldText.geographicCoverage}
              onChange={(e) => handleListFieldChange('geographicCoverage', e.target.value)}
              placeholder="NSW, VIC, QLD (comma-separated)"
            />
          </div>
        </section>

        {/* Company Statistics */}
        <section className="space-y-4">
          <h2 className="text-sm font-medium">Company Statistics</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="companyAge" className="text-meta">
                Company Age
              </Label>
              <ProfileInput
                isReadOnly={isProfileReadOnly}
                id="companyAge"
                value={profileForm.companyAge || ''}
                onChange={(e) =>
                  setProfileForm({ ...profileForm, companyAge: e.target.value })
                }
                placeholder="15 years"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="employeeCount" className="text-meta">
                Employee Count
              </Label>
              <ProfileInput
                isReadOnly={isProfileReadOnly}
                id="employeeCount"
                value={profileForm.employeeCount || ''}
                onChange={(e) =>
                  setProfileForm({ ...profileForm, employeeCount: e.target.value })
                }
                placeholder="250+"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="annualRevenue" className="text-meta">
                Annual Revenue
              </Label>
              <ProfileInput
                isReadOnly={isProfileReadOnly}
                id="annualRevenue"
                value={profileForm.annualRevenue || ''}
                onChange={(e) =>
                  setProfileForm({ ...profileForm, annualRevenue: e.target.value })
                }
                placeholder="$50M+"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="projectsCompleted" className="text-meta">
                Projects Completed
              </Label>
              <ProfileInput
                isReadOnly={isProfileReadOnly}
                id="projectsCompleted"
                value={profileForm.projectsCompleted || ''}
                onChange={(e) =>
                  setProfileForm({ ...profileForm, projectsCompleted: e.target.value })
                }
                placeholder="200+"
              />
            </div>
          </div>
        </section>

        {/* Key Differentiators */}
        <section className="space-y-4">
          <h2 className="text-sm font-medium">Key Differentiators</h2>
          <div className="space-y-2">
            <Label htmlFor="differentiators" className="text-meta">
              Differentiators
            </Label>
            <ProfileInput
              isReadOnly={isProfileReadOnly}
              id="differentiators"
              value={listFieldText.differentiators}
              onChange={(e) => handleListFieldChange('differentiators', e.target.value)}
              placeholder="Award-winning safety, ISO certified, Local expertise (comma-separated)"
            />
          </div>
        </section>

        {/* Safety Record */}
        <section className="space-y-4">
          <h2 className="text-sm font-medium">Safety Record</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="ltifr" className="text-meta">
                LTIFR
              </Label>
              <ProfileInput
                isReadOnly={isProfileReadOnly}
                id="ltifr"
                value={profileForm.safetyRecord?.ltifr || ''}
                onChange={(e) =>
                  setProfileForm({
                    ...profileForm,
                    safetyRecord: {
                      ...profileForm.safetyRecord,
                      ltifr: e.target.value,
                    },
                  })
                }
                placeholder="0.5"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="trifr" className="text-meta">
                TRIFR
              </Label>
              <ProfileInput
                isReadOnly={isProfileReadOnly}
                id="trifr"
                value={profileForm.safetyRecord?.trifr || ''}
                onChange={(e) =>
                  setProfileForm({
                    ...profileForm,
                    safetyRecord: {
                      ...profileForm.safetyRecord,
                      trifr: e.target.value,
                    },
                  })
                }
                placeholder="2.1"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="safetyMilestones" className="text-meta">
              Safety Milestones
            </Label>
            <ProfileInput
              isReadOnly={isProfileReadOnly}
              id="safetyMilestones"
              value={listFieldText.safetyMilestones}
              onChange={(e) => handleListFieldChange('safetyMilestones', e.target.value)}
              placeholder="1M hours LTI-free, Zero harm culture (comma-separated)"
            />
          </div>
        </section>

        {/* Certifications */}
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-medium">Certifications</h2>
            {!isProfileReadOnly && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label="Add certification"
                onClick={handleAddCertification}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add
              </Button>
            )}
          </div>
          <div className="space-y-3">
            {profileForm.certifications?.map((cert, index) => (
              <div key={index} className="flex gap-2 items-start">
                <div className="flex-1 space-y-2">
                  <ProfileInput
                    isReadOnly={isProfileReadOnly}
                    value={cert.name}
                    onChange={(e) =>
                      handleCertificationChange(index, 'name', e.target.value)
                    }
                    placeholder="Certification name"
                  />
                </div>
                <div className="w-48 space-y-2">
                  <ProfileInput
                    isReadOnly={isProfileReadOnly}
                    value={cert.expiryDate || ''}
                    onChange={(e) =>
                      handleCertificationChange(index, 'expiryDate', e.target.value)
                    }
                    placeholder="Expiry date (optional)"
                  />
                </div>
                {!isProfileReadOnly && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label="Remove certification"
                    onClick={() => handleRemoveCertification(index)}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
            {(!profileForm.certifications || profileForm.certifications.length === 0) && (
              <p className="text-meta">
                {isProfileReadOnly
                  ? 'No certifications added yet.'
                  : 'No certifications added yet. Click Add to create one.'}
              </p>
            )}
          </div>
        </section>

        {/* Entity details — the registration and contact facts tender
            schedules ask for verbatim */}
        <section className="space-y-4">
          <h2 className="text-sm font-medium">Entity details</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="acn" className="text-meta">
                ACN
              </Label>
              <ProfileInput
                isReadOnly={isProfileReadOnly}
                id="acn"
                value={profileForm.entityDetails?.acn || ''}
                onChange={(e) => handleEntityDetailChange('acn', e.target.value)}
                placeholder="123 456 789"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="primaryContactName" className="text-meta">
                Primary Contact Name
              </Label>
              <ProfileInput
                isReadOnly={isProfileReadOnly}
                id="primaryContactName"
                value={profileForm.entityDetails?.primaryContactName || ''}
                onChange={(e) =>
                  handleEntityDetailChange('primaryContactName', e.target.value)
                }
                placeholder="Jane Nguyen"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="registeredAddress" className="text-meta">
                Registered Address
              </Label>
              <ProfileInput
                isReadOnly={isProfileReadOnly}
                id="registeredAddress"
                value={profileForm.entityDetails?.registeredAddress || ''}
                onChange={(e) =>
                  handleEntityDetailChange('registeredAddress', e.target.value)
                }
                placeholder="Level 4, 100 Harbour Street, Sydney NSW 2000"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="postalAddress" className="text-meta">
                Postal Address
              </Label>
              <ProfileInput
                isReadOnly={isProfileReadOnly}
                id="postalAddress"
                value={profileForm.entityDetails?.postalAddress || ''}
                onChange={(e) => handleEntityDetailChange('postalAddress', e.target.value)}
                placeholder="PO Box 1234, Sydney NSW 2001"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="primaryContactEmail" className="text-meta">
                Primary Contact Email
              </Label>
              <ProfileInput
                isReadOnly={isProfileReadOnly}
                id="primaryContactEmail"
                type="email"
                value={profileForm.entityDetails?.primaryContactEmail || ''}
                onChange={(e) =>
                  handleEntityDetailChange('primaryContactEmail', e.target.value)
                }
                placeholder="tenders@yourcompany.com.au"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="primaryContactPhone" className="text-meta">
                Primary Contact Phone
              </Label>
              <ProfileInput
                isReadOnly={isProfileReadOnly}
                id="primaryContactPhone"
                type="tel"
                value={profileForm.entityDetails?.primaryContactPhone || ''}
                onChange={(e) =>
                  handleEntityDetailChange('primaryContactPhone', e.target.value)
                }
                placeholder="+61 2 9000 0000"
              />
            </div>
          </div>
        </section>

        {/* Insurances */}
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-medium">Insurances</h2>
            {!isProfileReadOnly && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label="Add insurance"
                onClick={handleAddInsurance}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add
              </Button>
            )}
          </div>
          <div className="space-y-3">
            {profileForm.insurances?.map((insurance, index) => (
              <div key={index} className="flex gap-2 items-start">
                <div className="flex-1 grid grid-cols-2 gap-2">
                  <ProfileInput
                    isReadOnly={isProfileReadOnly}
                    aria-label="Insurance Type"
                    value={insurance.type}
                    onChange={(e) => handleInsuranceChange(index, 'type', e.target.value)}
                    placeholder="Insurance type (e.g. Public liability)"
                  />
                  <ProfileInput
                    isReadOnly={isProfileReadOnly}
                    aria-label="Insurer"
                    value={insurance.insurer || ''}
                    onChange={(e) => handleInsuranceChange(index, 'insurer', e.target.value)}
                    placeholder="Insurer"
                  />
                  <ProfileInput
                    isReadOnly={isProfileReadOnly}
                    aria-label="Policy Number"
                    value={insurance.policyNumber || ''}
                    onChange={(e) =>
                      handleInsuranceChange(index, 'policyNumber', e.target.value)
                    }
                    placeholder="Policy number"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <ProfileInput
                      isReadOnly={isProfileReadOnly}
                      aria-label="Coverage Amount"
                      value={insurance.coverageAmount || ''}
                      onChange={(e) =>
                        handleInsuranceChange(index, 'coverageAmount', e.target.value)
                      }
                      placeholder="Coverage amount"
                    />
                    <ProfileInput
                      isReadOnly={isProfileReadOnly}
                      aria-label="Insurance Expiry Date"
                      type="date"
                      value={insurance.expiryDate || ''}
                      onChange={(e) =>
                        handleInsuranceChange(index, 'expiryDate', e.target.value)
                      }
                    />
                  </div>
                </div>
                {!isProfileReadOnly && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label="Remove insurance"
                    onClick={() => handleRemoveInsurance(index)}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
            {(!profileForm.insurances || profileForm.insurances.length === 0) && (
              <p className="text-meta">
                {isProfileReadOnly
                  ? 'No insurances added yet.'
                  : 'No insurances added yet. Click Add to create one.'}
              </p>
            )}
          </div>
        </section>

        {/* Referees */}
        <section className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-medium">Referees</h2>
            {!isProfileReadOnly && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label="Add referee"
                onClick={handleAddReferee}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add
              </Button>
            )}
          </div>
          <div className="space-y-3">
            {profileForm.referees?.map((referee, index) => (
              <div key={index} className="flex gap-2 items-start">
                <div className="flex-1 grid grid-cols-2 gap-2">
                  <ProfileInput
                    isReadOnly={isProfileReadOnly}
                    aria-label="Referee Name"
                    value={referee.name}
                    onChange={(e) => handleRefereeChange(index, 'name', e.target.value)}
                    placeholder="Referee name"
                  />
                  <ProfileInput
                    isReadOnly={isProfileReadOnly}
                    aria-label="Organisation"
                    value={referee.organisation || ''}
                    onChange={(e) =>
                      handleRefereeChange(index, 'organisation', e.target.value)
                    }
                    placeholder="Organisation"
                  />
                  <ProfileInput
                    isReadOnly={isProfileReadOnly}
                    aria-label="Referee Role"
                    value={referee.role || ''}
                    onChange={(e) => handleRefereeChange(index, 'role', e.target.value)}
                    placeholder="Role"
                  />
                  <ProfileInput
                    isReadOnly={isProfileReadOnly}
                    aria-label="Related Project"
                    value={referee.relatedProject || ''}
                    onChange={(e) =>
                      handleRefereeChange(index, 'relatedProject', e.target.value)
                    }
                    placeholder="Related project"
                  />
                  <ProfileInput
                    isReadOnly={isProfileReadOnly}
                    aria-label="Referee Phone"
                    type="tel"
                    value={referee.phone || ''}
                    onChange={(e) => handleRefereeChange(index, 'phone', e.target.value)}
                    placeholder="Phone"
                  />
                  <ProfileInput
                    isReadOnly={isProfileReadOnly}
                    aria-label="Referee Email"
                    type="email"
                    value={referee.email || ''}
                    onChange={(e) => handleRefereeChange(index, 'email', e.target.value)}
                    placeholder="Email"
                  />
                </div>
                {!isProfileReadOnly && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    aria-label="Remove referee"
                    onClick={() => handleRemoveReferee(index)}
                    className="text-muted-foreground hover:text-destructive"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            ))}
            {(!profileForm.referees || profileForm.referees.length === 0) && (
              <p className="text-meta">
                {isProfileReadOnly
                  ? 'No referees added yet.'
                  : 'No referees added yet. Click Add to create one.'}
              </p>
            )}
          </div>
        </section>

        {/* Save Button */}
        {!isProfileReadOnly && (
          <div className="flex justify-end mt-6">
            <Button type="submit" disabled={isSaving || !isProfileDirty}>
              {isSaving ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : profileSaved ? (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Saved
                </>
              ) : (
                'Save Profile'
              )}
            </Button>
          </div>
        )}
      </form>
    </div>
  )
}
